import { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Avatar } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupsIcon from '@mui/icons-material/Groups';
import { getRankings } from '../../api/rankingsApi';
import handleAgentName from '../../utils/userUtils';
import { handlePriceWithRM } from '../../utils/priceUtils';

const FONT = "'Poppins', sans-serif";
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const PHOTO_BASE = API_BASE.replace(/\/api\/?$/, '');
const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const RANK_COLOR = ['#F59E0B', '#94A3B8', '#B45309']; // gold, silver, bronze

const photoUrl = (p) => (p ? (/^https?:/i.test(p) ? p : `${PHOTO_BASE}${p}`) : undefined);

// One leaderboard: top 3 featured on the left, ranks 4–10 as a list on the right.
function Board({ title, subtitle, icon, items = [], type = 'agent', meta, empty }) {
    const featured = items.slice(0, 3);
    const rest = items.slice(3, 10);
    const nameOf = (r) => (type === 'team' ? r.groupName : (handleAgentName(r.firstName, r.lastName, r.alias) || 'Unknown'));
    const subOf = (r) => (type === 'team' ? `${r.members} member${r.members === 1 ? '' : 's'}` : (meta ? meta(r) : ''));
    const keyOf = (r, i) => (type === 'team' ? `t${r.groupId}` : `${r.userId}-${i}`);
    const initial = (r) => (r.firstName || r.alias || r.groupName || '?').charAt(0).toUpperCase();

    return (
        <Box sx={{ background: '#fff', borderRadius: 3, p: { xs: 2, md: 2.5 }, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                {icon}
                <Typography sx={{ fontWeight: 700, fontSize: 16, fontFamily: FONT }}>{title}</Typography>
            </Box>
            {subtitle && <Typography sx={{ fontSize: 11, color: '#94A3B8', fontFamily: FONT, mb: 1.5 }}>{subtitle}</Typography>}

            {items.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: '#94A3B8', textAlign: 'center', py: 3, fontFamily: FONT }}>{empty || 'No entries yet'}</Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1.5, md: 3 } }}>
                    {/* Featured top 3 */}
                    <Box sx={{ flex: { md: '0 0 340px' }, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {featured.map((r, i) => {
                            const color = RANK_COLOR[i] || '#94A3B8';
                            return (
                                <Box key={keyOf(r, i)} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1, borderRadius: 2, background: `${color}12`, border: `1px solid ${color}40` }}>
                                    <Box sx={{ width: 22, textAlign: 'center', fontWeight: 800, fontSize: 16, color, fontFamily: FONT }}>{i + 1}</Box>
                                    {type === 'agent'
                                        ? <Avatar src={photoUrl(r.profilePhotoUrl)} sx={{ width: 40, height: 40, fontSize: 15, bgcolor: '#e2e8f0', color: '#475569' }}>{initial(r)}</Avatar>
                                        : <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><GroupsIcon sx={{ color: '#fff', fontSize: 22 }} /></Box>}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0F172A', fontFamily: FONT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameOf(r)}</Typography>
                                        <Typography sx={{ fontSize: 10.5, color: '#94A3B8', fontFamily: FONT }}>{subOf(r)}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#16A34A', fontFamily: FONT, whiteSpace: 'nowrap' }}>{handlePriceWithRM(r.revenue)}</Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Ranks 4–10 */}
                    {rest.length > 0 && (
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {rest.map((r, i) => (
                                <Box key={keyOf(r, i + 3)} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.9, borderBottom: '1px solid #f1f5f9' }}>
                                    <Box sx={{ width: 20, textAlign: 'center', fontWeight: 700, fontSize: 12.5, color: '#94A3B8', fontFamily: FONT }}>{i + 4}</Box>
                                    {type === 'agent' && <Avatar src={photoUrl(r.profilePhotoUrl)} sx={{ width: 27, height: 27, fontSize: 11, bgcolor: '#e2e8f0', color: '#475569' }}>{initial(r)}</Avatar>}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#334155', fontFamily: FONT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameOf(r)}</Typography>
                                        {subOf(r) && <Typography sx={{ fontSize: 10, color: '#94A3B8', fontFamily: FONT }}>{subOf(r)}</Typography>}
                                    </Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#16A34A', fontFamily: FONT, whiteSpace: 'nowrap' }}>{handlePriceWithRM(r.revenue)}</Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default function RankingsPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        getRankings()
            .then(res => { if (!cancelled) setData(res.data); })
            .catch(() => { if (!cancelled) setData(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

    const d = data || {};
    const trophy = <EmojiEventsIcon sx={{ fontSize: 20, color: '#F59E0B' }} />;
    const saleMeta = (r) => `${r.salesCount} sale${r.salesCount === 1 ? '' : 's'}`;
    const monthMeta = (r) => `${MONTHS[r.month]} ${r.year} · ${saleMeta(r)}`;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, fontFamily: FONT }}>
            <Typography sx={{ fontWeight: 800, fontSize: 24, fontFamily: FONT, mb: 0.5 }}>Leaderboard</Typography>
            <Typography sx={{ fontSize: 13, color: '#64748B', fontFamily: FONT, mb: 3 }}>
                Ranked across all sales since our first deposit — 20 May 2024.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Board title="Top Sales of the Month" icon={trophy} items={d.topSalesMonth} meta={saleMeta} />
                <Board title="Best All-Time Monthly Sales" icon={trophy} items={d.bestMonthlySales} meta={monthMeta} empty="No sales yet" />
                <Board title="All-Time Rookie of the Month" icon={trophy} items={d.topRookieMonth} meta={saleMeta} empty="No rookies yet" />
                <Board title="All-Time Top Rookie of the Year" icon={trophy} items={d.allTimeTopRookie} meta={saleMeta} />
                <Board title="Million-Dollar Monthly Achievers" icon={trophy} items={d.millionAchievers} meta={(r) => `${MONTHS[r.month]} ${r.year}`} empty="No achievers yet" />
                <Board title="Multi-Million Sales Teams" icon={<GroupsIcon sx={{ fontSize: 20, color: '#1A8CFF' }} />} items={d.tenMillionTeams} type="team" empty="No teams yet" />
            </Box>
        </Box>
    );
}
